import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useI18N } from "@src/language/language-context";
import { updateShowOtaConfirmBackupProcessModal } from "@src/reducers/your-reducer";
import { getSingleUserUseCase } from "@src/cloud-connect/user-manager/use-cases/get-single-user-use-case";
import { cancelInstallationProcessUseCase } from "@src/cloud-connect/ota-update/use-cases/cancel-installation-process-use-case";
import {
  errorSnackbar,
  successSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import type { InstallAndActionBlock } from "@src/cloud-connect/ota-update/constants";
import { ActiveInstallationProcessPayload } from "@src/types/ota-update";
import { useFeatureFlag } from "@src/hooks/use-feature-flag";

interface UseOtaBackupProcessRequestApprovalForAdminProps {
  activeInstallationData?: ActiveInstallationProcessPayload | null;
  setInstallAndActionBlock: React.Dispatch<
    React.SetStateAction<InstallAndActionBlock | null>
  >;
}

export const useOtaBackupProcessRequestApprovalForAdmin = ({
  activeInstallationData,
  setInstallAndActionBlock,
}: UseOtaBackupProcessRequestApprovalForAdminProps) => {
  const dispatch = useDispatch();
  const i18n = useI18N();
  const [initiatorName, setInitiatorName] = useState<string | null>(null);
  const [isCancelRequestLoading, setIsCancelRequestLoading] = useState(false);
  const isDebugLoggingEnabled = useFeatureFlag("allow-debug", false);

  const texts = {
    title: i18n("modules.otaBackupProcessRequestApprovalForAdmin.title"),
    descriptionTemplate: i18n(
      "modules.otaBackupProcessRequestApprovalForAdmin.descriptionTemplate",
    ),
    reviewAndApproveButtonText: i18n(
      "modules.otaBackupProcessRequestApprovalForAdmin.reviewAndApproveButtonText",
    ),
    cancelRequestButtonText: i18n(
      "modules.otaBackupProcessRequestApprovalForAdmin.cancelRequestButtonText",
    ),
    cancelSuccessMessage: i18n(
      "modules.otaBackupProcessRequestApprovalForAdmin.cancelSuccessMessage",
    ),
    cancelFailureMessage: i18n(
      "modules.otaBackupProcessRequestApprovalForAdmin.cancelFailureMessage",
    ),
  };

  const handleReviewRequestAndApprove = () => {
    dispatch(updateShowOtaConfirmBackupProcessModal(true));
  };

  const handleCancelRequest = async () => {
    if (isCancelRequestLoading) {
      return;
    }

    const updateId = activeInstallationData?.update_id;

    if (!updateId) {
      return;
    }

    setIsCancelRequestLoading(true);

    try {
      const response = await cancelInstallationProcessUseCase(updateId);

      isDebugLoggingEnabled &&
        console.log(
          "OTA_DEBUG: Response from cancelInstallationProcessUseCase (backup request for admin):",
          response,
        );

      if (response.success) {
        setInstallAndActionBlock(null);
        successSnackbar(texts.cancelSuccessMessage, 5000);
        return;
      }

      errorSnackbar(texts.cancelFailureMessage, 5000);
      setIsCancelRequestLoading(false);
    } catch {
      errorSnackbar(texts.cancelFailureMessage, 5000);
      setIsCancelRequestLoading(false);
    }
  };

  useEffect(() => {
    async function fetchInitiatorName() {
      if (!activeInstallationData?.initiator_id) {
        setInitiatorName("");
        return;
      }

      isDebugLoggingEnabled &&
        console.log(
          "OTA_DEBUG: Fetching backup request initiator details for admin flow, ID:",
          activeInstallationData?.initiator_id,
        );

      const response = await getSingleUserUseCase(
        activeInstallationData?.initiator_id,
      );

      isDebugLoggingEnabled &&
        console.log(
          "OTA_DEBUG: Response from getSingleUserUseCase (backup initiator for admin flow):",
          response,
        );

      if (response.success && response.data?.display_name) {
        setInitiatorName(response.data?.display_name);
      } else {
        setInitiatorName("");
      }
    }

    fetchInitiatorName();
  }, [activeInstallationData?.initiator_id, isDebugLoggingEnabled]);

  const descriptionText = texts.descriptionTemplate.replace(
    "{username}",
    initiatorName || "",
  );

  return {
    texts,
    descriptionText,
    showContent: initiatorName !== null,
    handleReviewRequestAndApprove,
    handleCancelRequest,
    isCancelRequestLoading,
  };
};
